import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useDeleteKpiMutation } from '@/features/projects/project-details/hooks/use-delete-kpi-mutation';
import {
  derivativeKpisQueryKey,
  projectKpisQueryKey,
} from '@/features/projects/project-details/hooks/use-project-kpis';
import { isDerivativeKpi } from '@/features/projects/shared/utils/kpi-utils';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { DialogHeader } from '@/shared/components/ui/dialog/dialog';
import { toast } from '@/shared/hooks/use-toast';
import { KpiWithMetrics } from '@/shared/types/api';

interface DeleteDialogProps {
  kpi: KpiWithMetrics;
  projectId: number;
  name: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export const DeleteKpiDialog = ({
  kpi,
  projectId,
  name,
  isOpen,
  onOpenChange,
}: DeleteDialogProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('kpi', {
    keyPrefix: 'delete_kpi_dialog',
  });
  const onError = () => {
    toast({
      title: t('error_toast.title'),
      description: t('error_toast.description'),
      variant: 'destructive',
    });
  };

  const { mutate, isPending } = useDeleteKpiMutation({
    onError,
    onSettled: () => {
      onOpenChange(false);
      queryClient.invalidateQueries({
        queryKey: projectKpisQueryKey(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: derivativeKpisQueryKey(projectId),
      });
    },
  });

  const handleConfirm = () => {
    mutate(
      isDerivativeKpi(kpi)
        ? { id: kpi.id, type: 'derivative_kpi' }
        : kpi.kpi_budget_id
          ? { id: kpi.kpi_budget_id, type: 'assigned_kpi' }
          : { id: kpi.id, type: 'kpi' },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="gap-4 sm:max-w-[500px] md:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('title', { name })}</DialogTitle>
        </DialogHeader>
        <DialogDescription>{t('description')}</DialogDescription>
        <DialogFooter className="mt-6">
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              {t('actions.cancel')}
            </Button>
          </DialogClose>
          <Button
            onClick={handleConfirm}
            variant="destructive"
            disabled={isPending}
            className="min-w-[140px]"
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
              </>
            ) : (
              t('actions.delete')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
